import { useLocation, useNavigate } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import { addCoreCapability } from "../../actions/HomePageImages";

import React, {
    useState,
    ChangeEvent,
    FormEvent,
    useEffect,
} from "react";

interface LocationState {
    user?: any;
}

function AddCoreCapability() {
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
    const location = useLocation();
    const state = location.state as LocationState;
    const user = state?.user;

    if (
        location === null ||
        location.state === null ||
        location.state.user === null
    ) {
        navigate("/login");
    }

    const [topText, setTopText] = useState<string>("");
    const [bottomText, setBottomText] = useState<string>("");
    const [rightText, setRightText] = useState<string>("");
    const [position, setPosition] = useState<string>("");

    const [image, setImage] = useState<File | null>(null);

    const [imageError, setImageError] = useState<string>("");
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");

    const [submitMessage, setSubmitMessage] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const validateImage = (file: File): string | null => {
        const validTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
        ];

        if (!validTypes.includes(file.type)) {
            return "Only JPG, PNG or WebP files are allowed.";
        }

        if (file.size > 2 * 1024 * 1024) {
            return "File size must be less than or equal to 2MB.";
        }

        return null;
    };

    const handleImageChange = (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];

        if (!file) {
            return;
        }

        const error = validateImage(file);

        if (error) {
            setImageError(error);
            setImage(null);
            setImagePreviewUrl("");
            return;
        }

        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
        }

        const url = URL.createObjectURL(file);

        setImage(file);
        setImageError("");
        setImagePreviewUrl(url);
    };

    const removeImage = () => {
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
        }

        setImage(null);
        setImagePreviewUrl("");
        setImageError("");
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        if (
            !topText ||
            !bottomText ||
            !rightText ||
            !position ||
            !image
        ) {
            setSubmitMessage(
                "Please fill in all fields and select an image."
            );
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage("");

        try {
            await addCoreCapability({
                top_text: topText,
                bottom_text: bottomText,
                right_text: rightText,
                position: Number(position),
                image,
            });

            navigate("/dashboard/home/images", {
                state: { user },
            });
        } catch (error) {
            setSubmitMessage(
                "An error occurred while adding the core capability."
            );

            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        return () => {
            if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [imagePreviewUrl]);

    return (
        <div>
            <AdminHeader
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                user={user}
            />

            <AdminNav
                collapsed={isCollapsed}
                setCollapsed={setIsCollapsed}
                user={user}
            />

            <div
                className={`main-admin-page ${
                    isCollapsed ? "collapsed" : "expanded"
                }`}
            >
                <div className="main-admin-page-inner">
                    <form onSubmit={handleSubmit}>
                        <h2>Add New Core Capability</h2>

                        <div className="content-form-field">
                            <input
                                type="text"
                                className="input-email"
                                placeholder="Top Text"
                                value={topText}
                                onChange={e =>
                                    setTopText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <input
                                type="text"
                                className="input-email"
                                placeholder="Bottom Text"
                                value={bottomText}
                                onChange={e =>
                                    setBottomText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <label>Right Text</label>

                            <textarea
                                className="input-email"
                                rows={7}
                                value={rightText}
                                onChange={e =>
                                    setRightText(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <input
                                type="number"
                                className="input-email"
                                placeholder="Position"
                                value={position}
                                onChange={e =>
                                    setPosition(e.target.value)
                                }
                                min="1"
                                required
                            />
                        </div>

                        <div className="content-form-field">
                            <br />
                            <label>Image</label>
                            <br />

                            <input
                                type="file"
                                accept=".jpg,.jpeg,.png,.webp"
                                onChange={handleImageChange}
                                style={{
                                    marginTop: "10px",
                                }}
                            />

                            {imageError && (
                                <div
                                    style={{
                                        color: "red",
                                    }}
                                >
                                    {imageError}
                                </div>
                            )}

                            {imagePreviewUrl && (
                                <div
                                    style={{
                                        marginTop: "10px",
                                    }}
                                >
                                    <strong>Preview</strong>
                                    <br />

                                    <img
                                        src={imagePreviewUrl}
                                        alt="Core Capability Preview"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "300px",
                                        }}
                                    />

                                    <br />

                                    <button
                                        type="button"
                                        onClick={removeImage}
                                    >
                                        Remove Image
                                    </button>
                                </div>
                            )}
                        </div>

                        <br />

                        <div className="content-form-button">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting
                                    ? "Submitting..."
                                    : "Save Changes"}
                            </button>
                        </div>

                        {submitMessage && (
                            <div
                                style={{
                                    marginTop: "10px",
                                    color: "red",
                                }}
                            >
                                {submitMessage}
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AddCoreCapability;
